import { useState } from "react";
import { toast } from "sonner";
import {
  Share2,
  Copy,
  Check,
  X,
  Mail,
  Twitter,
  Linkedin,
  MessageCircle,
} from "lucide-react";

/**
 * ShareApp — single-source share entry point.
 *
 * Exposes:
 *  - <ShareAppLink medium="sidebar"> / "completed" / "empty" / "settings" />  — inline trigger
 *  - <ShareAppModal open onClose medium={...} />                              — controlled modal
 *
 * Every share URL carries UTM tags so first-touch attribution flows to Mongo
 * once we wire up analytics. `medium` distinguishes where in the app the share
 * was initiated from so we can tell which placement actually converts.
 */

const SHARE_BASE = "https://break-it-down.net";
const SHARE_MESSAGE =
  "BreakItDown turns any big task into a clear checklist with AI. Free, no signup hoops.";
const HASHTAG = "BreakItDown";

function buildUrl(medium) {
  const params = new URLSearchParams({
    utm_source: "app_share",
    utm_medium: medium || "unknown",
    utm_campaign: "referral",
  });
  return `${SHARE_BASE}/?${params}`;
}

async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export function ShareAppLink({ medium = "unknown", className = "", children, dataTestId }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={
          className ||
          "inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-[#002FA7] transition-colors"
        }
        data-testid={dataTestId || `share-app-trigger-${medium}`}
      >
        <Share2 className="w-3.5 h-3.5" strokeWidth={1.5} />
        {children || "Share BreakItDown"}
      </button>
      <ShareAppModal open={open} onClose={() => setOpen(false)} medium={medium} />
    </>
  );
}

export function ShareAppModal({ open, onClose, medium = "modal" }) {
  const [copied, setCopied] = useState(false);
  if (!open) return null;

  const shareUrl = buildUrl(medium);
  const encodedMessage = encodeURIComponent(SHARE_MESSAGE);
  const encodedUrl = encodeURIComponent(shareUrl);

  const handleCopy = async () => {
    const ok = await copyToClipboard(shareUrl);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
      toast.success("Link copied to clipboard");
    } else {
      toast.error("Couldn't copy — try long-press the link");
    }
  };

  const handleNativeShare = async () => {
    if (!navigator.share) return;
    try {
      await navigator.share({
        title: "BreakItDown",
        text: SHARE_MESSAGE,
        url: shareUrl,
      });
      onClose();
    } catch {
      /* user cancelled — silent */
    }
  };

  const hasNativeShare = typeof navigator !== "undefined" && !!navigator.share;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="share-app-modal"
    >
      <div
        className="bg-white border border-zinc-200 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="border-b border-zinc-200 px-5 py-4 flex items-center justify-between">
          <h2 className="text-base font-semibold tracking-tight flex items-center gap-2">
            <Share2 className="w-4 h-4 text-[#002FA7]" strokeWidth={1.75} />
            Share BreakItDown
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-zinc-500 hover:text-zinc-900"
            aria-label="Close"
            data-testid="share-app-close"
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="p-5 space-y-4">
          <p className="text-sm text-zinc-600 leading-relaxed">
            Know someone drowning in vague to-dos? Send them BreakItDown — free, AI breakdowns
            included, no signup hoops.
          </p>

          {/* Copy link */}
          <div className="flex items-stretch border border-zinc-300">
            <input
              type="text"
              value={shareUrl}
              readOnly
              onFocus={(e) => e.target.select()}
              className="flex-1 px-3 py-2.5 text-sm bg-zinc-50 outline-none font-mono truncate"
              data-testid="share-app-url-input"
            />
            <button
              type="button"
              onClick={handleCopy}
              className="flex items-center gap-1.5 px-3 sm:px-4 bg-zinc-900 hover:bg-[#002FA7] text-white text-xs font-medium uppercase tracking-[0.1em] transition-colors whitespace-nowrap"
              data-testid="share-app-copy"
            >
              {copied ? (
                <>
                  <Check className="w-3.5 h-3.5" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="w-3.5 h-3.5" />
                  Copy
                </>
              )}
            </button>
          </div>

          {/* Native Web Share on mobile / supported desktops */}
          {hasNativeShare && (
            <button
              type="button"
              onClick={handleNativeShare}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 border border-zinc-300 hover:border-zinc-900 text-sm font-medium text-zinc-900 transition-colors"
              data-testid="share-app-native"
            >
              <Share2 className="w-4 h-4" strokeWidth={1.75} />
              Share via your apps…
            </button>
          )}

          {/* Channels */}
          <div className="grid grid-cols-4 gap-2">
            <ShareChannel
              href={`mailto:?subject=${encodeURIComponent("BreakItDown — break big tasks down")}&body=${encodedMessage}%0A%0A${encodedUrl}`}
              label="Email"
              Icon={Mail}
              testId="share-app-email"
            />
            <ShareChannel
              href={`https://twitter.com/intent/tweet?text=${encodedMessage}&url=${encodedUrl}&hashtags=${HASHTAG}`}
              label="X"
              Icon={Twitter}
              testId="share-app-twitter"
            />
            <ShareChannel
              href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`}
              label="LinkedIn"
              Icon={Linkedin}
              testId="share-app-linkedin"
            />
            <ShareChannel
              href={`https://wa.me/?text=${encodedMessage}%20${encodedUrl}`}
              label="WhatsApp"
              Icon={MessageCircle}
              testId="share-app-whatsapp"
            />
          </div>

          <p className="text-[11px] text-zinc-400 leading-relaxed">
            Thanks for helping spread the word — every share keeps BreakItDown free for everyone.
          </p>
        </div>
      </div>
    </div>
  );
}

function ShareChannel({ href, label, Icon, testId }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="flex flex-col items-center justify-center gap-1.5 py-3 border border-zinc-200 hover:border-zinc-900 hover:bg-zinc-50 transition-colors text-xs text-zinc-700"
      data-testid={testId}
    >
      <Icon className="w-4 h-4" strokeWidth={1.75} />
      {label}
    </a>
  );
}
